import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Hourglass, PlayCircle, ChevronLeft, ChevronRight, Mail, Calendar, Volume2, Clock, MessageCircle } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import '../styles/BookedPage.css';

const BookedPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(4);
  const carouselRef = useRef(null);
  const videoRef = useRef(null);

  // Responsive slide config: slideWidth%, peekOffset%, maxPer (items to subtract from length for max)
  const getSlideConfig = useCallback(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    if (w <= 768) return { slideWidth: 75, peekOffset: 12.5, perView: 1 };
    if (w <= 1024) return { slideWidth: 45, peekOffset: 5, perView: 2 };
    return { slideWidth: 30, peekOffset: 5, perView: 3 };
  }, []);

  const [slideConfig, setSlideConfig] = useState(getSlideConfig);

  useEffect(() => {
    setIsVisible(true);
    const handleResize = () => setSlideConfig(getSlideConfig());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getSlideConfig]);

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (video) {
      video.play()
        .then(() => {
          setIsPlaying(true);
          video.setAttribute('controls', 'controls');
        })
        .catch((err) => {
          console.error('Video play failed:', err);
          video.muted = true;
          video.play()
            .then(() => {
              setIsPlaying(true);
              video.setAttribute('controls', 'controls');
            })
            .catch((e) => console.error('Muted play also failed:', e));
        });
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  // Transformation images for carousel
  const transformations = [
    {
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/842gjd54_matt%20transformation.png",
      alt: "Matt transformation - before and after"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/wotcn5uh_jack%20testimonial.jpeg",
      alt: "Jack transformation - before and after"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_working-men-gains/artifacts/okv01bvp_Mario%20transformation%20.jpeg",
      alt: "Mario transformation - before and after"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_a2565e66-8e7b-4f8f-a45b-b322b9f6164a/artifacts/ehzc26b6_IMG_7892%202.JPG",
      alt: "Client transformation - before and after"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_a2565e66-8e7b-4f8f-a45b-b322b9f6164a/artifacts/4nfsovp7_Copy%20of%20Elegant%20Beige%20Launching%20Soon%20Instagram%20Reel%202.png.jpg",
      alt: "Client transformation - before and after"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_a2565e66-8e7b-4f8f-a45b-b322b9f6164a/artifacts/xvz513e5_IMG_4849.jpg",
      alt: "Client transformation - before and after"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_a2565e66-8e7b-4f8f-a45b-b322b9f6164a/artifacts/tzn71aow_IMG_4853.JPG",
      alt: "Client transformation - before and after"
    },
    {
      image: "/transform-8144.jpg",
      alt: "Client transformation - before and after"
    },
    {
      image: "/transform-8146.jpg",
      alt: "Client transformation - before and after"
    },
  ];

  const slideLeft = () => {
    setCurrentSlide(prev => Math.max(0, prev - 1));
  };

  const slideRight = () => {
    setCurrentSlide(prev => Math.min(transformations.length - slideConfig.perView, prev + 1));
  };

  const canSlideLeft = currentSlide > 0;
  const canSlideRight = currentSlide < transformations.length - slideConfig.perView;

  return (
    <div className="booked-page">
      {/* Hero Section */}
      <section className="booked-hero-section">
        <div className="booked-hero-content">
          <div className={`booked-confirmation ${isVisible ? 'fade-in' : ''}`}>
            <div className="booked-check-icon">
              <Hourglass size={42} strokeWidth={2.5} />
            </div>
            <h1 className="booked-headline">Your Call Is Almost Confirmed</h1>
            <p className="booked-subtext">
              Your strategy call request has been received.
            </p>
            <p className="booked-subtext-secondary">
              Before the call, please complete the steps below so I can review your details and confirm everything looks like a good fit before our session.
            </p>
          </div>
        </div>
      </section>

      {/* Steps Container */}
      <section className="booked-steps-section">
        <div className="booked-steps-container">
          
          {/* Step 1 - Video */}
          <AnimatedSection animation="fadeUp" className="booked-step-wrapper">
            <div className="booked-step">
              <div className="booked-step-header">
                <span className="booked-step-number">1</span>
                <h3 className="booked-step-title">Watch This Before Our Call</h3>
              </div>
              <div className="booked-step-content">
                <div className="booked-video-wrapper">
                  <div className="booked-video-player">
                    <video 
                      ref={videoRef}
                      id="booked-video"
                      className="booked-video-element"
                      playsInline
                      preload="auto"
                      onPlay={handleVideoPlay}
                    >
                      <source 
                        src="/pre-call-video.mp4" 
                        type="video/mp4" 
                      />
                      Your browser does not support the video tag.
                    </video>
                    {!isPlaying && (
                      <div 
                        className="booked-play-overlay" 
                        onClick={handlePlayClick}
                        data-testid="booked-play-button"
                      >
                        <div className="booked-play-circle">
                          <PlayCircle className="booked-play-icon" size={70} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <p className="booked-step-description">
                  This explains exactly how the coaching works and what we'll cover on the call.
                </p>
              </div>
            </div>
          </AnimatedSection>

          {/* Step 2 - Calendar */}
          <AnimatedSection animation="fadeUp" delay={0.1} className="booked-step-wrapper">
            <div className="booked-step">
              <div className="booked-step-header">
                <span className="booked-step-number">2</span>
                <h3 className="booked-step-title">Add The Call To Your Calendar</h3>
              </div>
              <div className="booked-step-content">
                <div className="booked-step-text-block">
                  <p>Check your email for the calendar invite and Google Meet link.</p>
                  <p>Make sure the call is saved so you don't miss it.</p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Step 3 - Be Ready */}
          <AnimatedSection animation="fadeUp" delay={0.2} className="booked-step-wrapper">
            <div className="booked-step">
              <div className="booked-step-header">
                <span className="booked-step-number">3</span>
                <h3 className="booked-step-title">Be Ready For The Call</h3>
              </div>
              <div className="booked-step-content">
                <ul className="booked-step-list">
                  <li>
                    <Volume2 className="booked-step-list-icon" size={20} />
                    <span>Sit somewhere quiet</span>
                  </li>
                  <li>
                    <Clock className="booked-step-list-icon" size={20} />
                    <span>Have 30 minutes free</span>
                  </li>
                  <li>
                    <MessageCircle className="booked-step-list-icon" size={20} />
                    <span>Bring questions about your goals</span>
                  </li>
                </ul>
              </div>
            </div>
          </AnimatedSection>

          {/* Step 4 - Message */}
          <AnimatedSection animation="fadeUp" delay={0.3} className="booked-step-wrapper">
            <div className="booked-step">
              <div className="booked-step-header">
                <span className="booked-step-number">4</span>
                <h3 className="booked-step-title">Expect A Message Before The Call</h3>
              </div>
              <div className="booked-step-content">
                <div className="booked-step-text-block">
                  <p>I'll review your details before the call.</p>
                  <p>If everything looks like a good fit, I'll reach out shortly before the session.</p>
                </div>
              </div>
            </div>
          </AnimatedSection>

        </div>
      </section>

      {/* Client Results - Carousel */}
      <section className="booked-results-section">
        <div className="booked-results-container">
          <AnimatedSection animation="fadeUp">
            <h2 className="booked-section-title">35+ Men Transformed</h2>
          </AnimatedSection>
          
          <div className="booked-carousel-wrapper">
            <button 
              className={`booked-carousel-arrow booked-arrow-left ${!canSlideLeft ? 'disabled' : ''}`}
              onClick={slideLeft}
              disabled={!canSlideLeft}
              aria-label="Previous"
            >
              <ChevronLeft size={32} />
            </button>

            <div className="booked-carousel-container" ref={carouselRef}>
              <div 
                className="booked-carousel-track"
                style={{ transform: `translateX(-${Math.max(0, Math.min(transformations.length * slideConfig.slideWidth - 100, currentSlide * slideConfig.slideWidth - slideConfig.peekOffset))}%)` }}
              >
                {transformations.map((item, index) => (
                  <div key={index} className="booked-carousel-slide">
                    <Card className="booked-transformation-card">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.alt}
                          className="booked-transformation-image"
                        />
                      ) : (
                        <div className="booked-transformation-placeholder">
                          <span>+</span>
                        </div>
                      )}
                    </Card>
                  </div>
                ))}
              </div>
              
              <div className="booked-carousel-fade booked-fade-left"></div>
              <div className="booked-carousel-fade booked-fade-right"></div>
            </div>

            <button 
              className={`booked-carousel-arrow booked-arrow-right ${!canSlideRight ? 'disabled' : ''}`}
              onClick={slideRight}
              disabled={!canSlideRight}
              aria-label="Next"
            >
              <ChevronRight size={32} />
            </button>
          </div>
        </div>
      </section>

      {/* Proof Gallery Section */}
      <section className="booked-proof-section">
        <div className="booked-proof-container">
          <AnimatedSection animation="fadeUp">
            <h2 className="booked-section-title">Hear It From The Boys</h2>
          </AnimatedSection>
          <div className="booked-proof-grid">
            {[
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/y4p97h2v_IMG_4976%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/siwcouxf_IMG_4977%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/2vxq2ck9_IMG_4978%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/ohoo6uvr_IMG_4979%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/mrjvbb8l_IMG_4980%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/rmshrbb3_IMG_4981%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/z93mbm2a_IMG_4982%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/ui6hnyea_IMG_4983%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/w8tm9v8d_IMG_4984%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/goju7io1_IMG_4985%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/erefyogd_IMG_4986%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/75z124n7_IMG_4987%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/f0zjibkc_IMG_4988%202.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/k0pgj1sz_IMG_5001.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/y5tkux37_IMG_5003.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/s0e435qi_IMG_5004.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/3o7mwzrm_IMG_5005.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/76e7yolk_IMG_5006.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/851gzjzt_IMG_5008.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/p5ptec4v_IMG_5009.jpg",
              "https://customer-assets.emergentagent.com/job_76507d12-b7b1-48a8-b79e-c338a923a3da/artifacts/n652ecun_IMG_5011%202.jpg",
            ].map((src, i) => (
              <div key={i} className="booked-proof-item">
                <img src={src} alt={`Client proof ${i + 1}`} className="booked-proof-image" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="booked-footer">
        <div className="booked-footer-content">
          <p className="booked-footer-text">© 2025 Rudi Garcia Coaching. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BookedPage;
